import React, { useContext, useEffect, useState } from 'react';
import '../../css/WeatherIcon.css';
import { SettingsContext } from '../MenuIcon/ListItemContent/SettingsContext';

function WeatherSymbol({ icon = '03d' }) {
  const code = icon.slice(0, 2);
  const cloudy = !['01', '50'].includes(code);
  return (
    <svg className="weatherImage" viewBox="0 0 48 48" aria-hidden="true">
      {['01', '02'].includes(code) && (icon.endsWith('n')
        ? <path d="M27 5a14 14 0 1 0 15 20A14 14 0 0 1 27 5Z" fill="#dce8f1" stroke="#627b94" strokeWidth="1.5" />
        : <><circle cx="22" cy="19" r="9" fill="#f6c552" /><path d="M22 3v4m0 24v4M6 19h4m24 0h4M11 8l3 3m16 16 3 3M11 30l3-3M30 11l3-3" stroke="#d79a20" strokeWidth="2" strokeLinecap="round" /></>)}
      {cloudy && <path d="M12 34a8 8 0 0 1-1-16 11 11 0 0 1 21-1 8.5 8.5 0 1 1 4 17Z" fill="#e6edf2" stroke="#607d8b" strokeWidth="1.6" />}
      {['09', '10'].includes(code) && <path d="m15 38-2 5m10-5-2 5m10-5-2 5" stroke="#3c8db8" strokeWidth="2.4" strokeLinecap="round" />}
      {code === '11' && <path d="m25 29-8 11h7l-3 7 12-13h-8l4-5Z" fill="#e9ad25" />}
      {code === '13' && <path d="M15 38v7m-3-5 6 3m-6 0 6-3m13-2v7m-3-5 6 3m-6 0 6-3" stroke="#3c8db8" strokeWidth="1.5" strokeLinecap="round" />}
      {code === '50' && <path d="M9 17h30M5 24h33M11 31h30" stroke="#607d8b" strokeWidth="3" strokeLinecap="round" />}
    </svg>
  );
}

export default function WeatherIcon() {
  const settings = useContext(SettingsContext);
  const { locationZip, setWeatherLoaded, setValidZip } = settings;
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState('loading');
  const [requestVersion, setRequestVersion] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    const zip = String(locationZip);
    localStorage.setItem('userZipcode', zip);
    setWeatherLoaded(false);
    setWeather(null);
    setRetryAfter(0);
    if (!/^\d{5}$/.test(zip)) {
      setStatus('invalid');
      setValidZip(false);
      return;
    }

    setStatus('loading');
    setValidZip(true);
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 40000);
    const url = new URL('/backend/api/weather', window.location.origin);
    url.searchParams.set('zip', zip);
    (async () => {
      try {
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'omit', headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!active) return;
        if (!response.ok || data.cod) {
          const invalid = [400, 404].includes(response.status);
          setStatus(invalid ? 'invalid' : 'error');
          if (invalid) setValidZip(false);
          const retry = (response.headers.get('Retry-After') || '').trim();
          const retryDate = Date.parse(retry);
          const seconds = /^\d+$/.test(retry) ? Number(retry) : Number.isFinite(retryDate) ? (retryDate - Date.now()) / 1000 : 0;
          if (Number.isFinite(seconds) && seconds > 0) setRetryAfter(Math.min(Math.ceil(seconds), 3600));
          return;
        }
        if (typeof data.current?.temp !== 'number' || !Number.isFinite(data.current.temp)) throw new Error('Missing temperature');
        setWeather(data);
        setStatus('ready');
        setWeatherLoaded(true);
        setValidZip(true);
      } catch {
        if (active) setStatus('error');
      } finally {
        window.clearTimeout(timeout);
      }
    })();
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [locationZip, requestVersion, setWeatherLoaded, setValidZip]);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = window.setTimeout(() => setRetryAfter(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [retryAfter]);

  function openSettings() {
    const visible = !settings.settingsVisible;
    settings.setDropDownClick(false);
    settings.setSearchBarVisible(!visible);
    settings.setSettingsVisible(visible);
    settings.setChangeBackgroundVisible(false);
  }

  const condition = weather?.current?.weather?.[0];
  const source = weather?.meta?.source === 'metno' ? 'MET Norway' : weather?.meta?.source === 'nws' ? 'NWS' : null;
  const forecast = weather?.meta?.kind === 'forecast';
  const celsius = weather ? weather.current.temp - 273.15 : null;
  const temperature = weather ? `${(settings.showInCelcius ? celsius : celsius * 1.8 + 32).toFixed(1)}°${settings.showInCelcius ? 'C' : 'F'}` : '';
  const timestamp = weather?.meta?.updatedAt;
  const description = condition?.description || 'Weather';
  const detail = weather ? `${forecast ? 'Forecast' : 'Observation'} · ${description}${timestamp ? ` · ${new Date(timestamp).toLocaleString()}` : ''}` : '';
  const message = status === 'loading' ? 'Loading weather…' : status === 'invalid' ? (settings.settingsVisible ? 'Invalid ZIP code' : 'Set a valid ZIP code') : 'Weather unavailable';

  return (
    <div className="weatherIconDiv">
      <button className="weatherSettingsButton" type="button" onClick={openSettings} title={detail || 'Weather settings'} aria-label={weather ? `${temperature}${forecast ? ' forecast' : ''} · ${description}. Open weather settings` : `${message}. Open weather settings`}>
        {status === 'ready' && weather ? <><WeatherSymbol icon={condition?.icon} /><span className="weatherReading"><span className="temperatureReading">{temperature}</span>{forecast && <small className="weatherEstimate">Forecast</small>}</span></> : <span className="invalidZipCode" role="status">{message}</span>}
      </button>
      {status === 'ready' && weather && <div className="weatherCredits"><a href="https://weather.parkert.dev/" target="_blank" rel="noreferrer">Weather API</a>{source && <><span> · </span><a href={weather.meta.sourceUrl || 'https://weather.parkert.dev/api/v1/sources'} target="_blank" rel="noreferrer" title={weather.meta.attribution}>{source}</a></>}{weather.meta?.source === 'metno' && <><span> · </span><a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">CC BY 4.0</a></>}</div>}
      {status === 'error' && <button className="weatherRetry" type="button" onClick={() => setRequestVersion(value => value + 1)} disabled={retryAfter > 0}>{retryAfter > 0 ? `Retry in ${retryAfter}s` : 'Retry weather'}</button>}
    </div>
  );
}
